import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Package, Calendar, FileText, CreditCard, MessageSquare, Wrench,
  Camera, Tag, User, LogOut, ChevronRight, Menu, X, Send, Check, Pen,
  Lock, Bell, FolderOpen, BookOpen, Paperclip, Download, FileImage, Eye, Upload
} from 'lucide-react';
import axios from 'axios';
import {
  AppointmentsSection, TeamSection, CleaningSection, WarrantyBlock, AccountSettingsSection
} from '../components/portal/PortalSections';
import { useTheme, ThemeToggle } from '../components/ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const folderLabels = {
  general: 'General',
  'pre-construction': 'Pre-Construction',
  installation: 'Installation',
  completion: 'Completion',
  warranty: 'Warranty',
};

const docTypeColors = {
  contract: '#C9A84C',
  proposal: '#6366f1',
  warranty: '#22c55e',
  invoice: '#f59e0b',
  other: 'rgba(255,255,255,0.5)',
};

const maintenanceStatusColors = {
  scheduled: { bg: 'bg-indigo-500/10', text: 'text-indigo-400' },
  in_progress: { bg: 'bg-yellow-500/10', text: 'text-yellow-500' },
  completed: { bg: 'bg-green-500/10', text: 'text-green-500' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-400' },
};

export default function ClientPortalPage() {
  const [activeSection, setActiveSection] = useState('home');
  const [profile, setProfile] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uanCode, setUanCode] = useState('');
  const [uanLoading, setUanLoading] = useState(false);
  const [uanError, setUanError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('client_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadProfile = useCallback(async () => {
    if (!token) { navigate('/portal'); return; }
    try {
      const res = await axios.get(`${API}/client/profile`, { headers });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('client_token'); navigate('/portal'); }
    }
  }, [token, navigate]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const logout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_name');
    navigate('/portal');
  };

  const activateUAN = async () => {
    setUanError('');
    setUanLoading(true);
    try {
      await axios.post(`${API}/client/activate`, { code: uanCode }, { headers });
      setUanCode('');
      loadProfile();
    } catch (err) {
      setUanError(err.response?.data?.detail || 'Invalid code');
    } finally { setUanLoading(false); }
  };

  const tier = profile?.access_tier || (profile?.is_activated ? 2 : 1);
  const isActivated = tier >= 2;
  const [theme, toggleTheme] = useTheme('drt-portal-theme');

  const navGroups = [
    { title: 'Overview', items: [
      { id: 'home', label: 'Dashboard', icon: Home },
      { id: 'inbox', label: 'Messages', icon: MessageSquare },
    ]},
    { title: 'My Theater', items: [
      { id: 'gallery', label: 'My Theater', icon: Camera, locked: !isActivated },
      { id: 'appointments', label: 'Appointments', icon: Calendar },
      { id: 'team', label: 'My Team', icon: User },
    ]},
    { title: 'Services', items: [
      { id: 'services', label: 'Care Plans', icon: Package },
      { id: 'maintenance', label: 'Warranty & Maintenance', icon: Wrench },
      { id: 'cleaning', label: 'Cleaning', icon: Tag },
      { id: 'booking', label: 'Book Service', icon: Calendar },
    ]},
    { title: 'Documents & Files', items: [
      { id: 'library', label: 'Document Library', icon: FolderOpen },
      { id: 'booklets', label: 'Theater Booklets', icon: BookOpen },
      { id: 'documents', label: 'My Documents', icon: FileText },
    ]},
    { title: 'Account', items: [
      { id: 'payments', label: 'Payments', icon: CreditCard },
      { id: 'promotions', label: 'Promotions', icon: Tag },
      { id: 'profile', label: 'Account Settings', icon: User },
    ]},
  ];
  const navItems = navGroups.flatMap(g => g.items);

  return (
    <div className={`min-h-screen bg-[#0f0f10] flex ${theme === 'light' ? 'theme-light' : ''}`} data-testid="client-portal">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0d0709] border-r border-[#c9a84c]/10 transform transition-transform duration-300 lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-[#c9a84c]/10">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-icon.png" alt="DreamRoom Theaters" className="w-10 h-10 object-contain" />
            <div>
              <h2 className="font-playfair text-[#C9A84C] text-sm tracking-wide">Client Portal</h2>
              <p className="text-white/25 font-manrope text-[10px]">{profile?.name || 'Loading...'}</p>
            </div>
          </div>
        </div>

        <nav className="p-3 pb-24 space-y-4 overflow-y-auto max-h-[calc(100vh-100px)]">
          {navGroups.map(group => (
            <div key={group.title}>
              <p className="px-4 pb-1.5 text-[9px] uppercase tracking-[0.25em] text-white/20 font-manrope">{group.title}</p>
              <div className="space-y-0.5">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 font-manrope text-xs transition-all duration-200 ${active ? 'bg-[#1A0F12] text-[#C9A84C] border-l-[3px] border-[#C9A84C] rounded-r-sm' : 'text-white/45 hover:text-white/70 hover:bg-[#1A0F12]/50 rounded-sm border-l-[3px] border-transparent'}`}
                      data-testid={`nav-${item.id}`}
                    >
                      <Icon size={15} strokeWidth={1.5} />
                      <span className="uppercase tracking-widest flex-1 text-left">{item.label}</span>
                      {item.locked && <Lock size={10} className="text-white/15" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#c9a84c]/10">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 text-white/30 hover:text-red-400 font-manrope text-xs uppercase tracking-widest transition-colors" data-testid="portal-logout">
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="border-b border-[#c9a84c]/10 bg-[#0A0608]/95 backdrop-blur-md sticky top-0 z-30 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white/40 hover:text-[#C9A84C] transition-colors">
              <Menu size={20} />
            </button>
            <h1 className="font-playfair text-white text-lg capitalize tracking-wide">{navItems.find(n => n.id === activeSection)?.label || 'Dashboard'}</h1>
          </div>
          <div className="flex items-center gap-3">
            {isActivated ? (
              <div className="hidden sm:flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-sm px-3 py-1.5" data-testid="tier-badge">
                <span className="text-green-500 font-manrope text-[10px] uppercase tracking-widest">Tier 2 — Full Project Access</span>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-sm px-3 py-1.5" data-testid="tier-badge">
                <span className="text-[#C9A84C] font-manrope text-[10px] uppercase tracking-widest">Tier 1 — Standard Access</span>
              </div>
            )}
            <ThemeToggle theme={theme} onToggle={toggleTheme} testId="portal-theme-toggle" />
          </div>
        </header>

        <div className="p-6 max-w-5xl">
          {activeSection === 'home' && <HomeDashboard profile={profile} setActiveSection={setActiveSection} isActivated={isActivated} headers={headers} />}
          {activeSection === 'services' && <ServicesSection isActivated={true} headers={headers} profile={profile} />}
          {activeSection === 'booking' && <BookingSection headers={headers} profile={profile} />}
          {activeSection === 'inbox' && <InboxSection headers={headers} profile={profile} />}
          {activeSection === 'library' && <LibrarySection headers={headers} isActivated={true} />}
          {activeSection === 'booklets' && <BookletsSection headers={headers} isActivated={true} />}
          {activeSection === 'documents' && <DocumentsSection isActivated={true} headers={headers} profile={profile} />}
          {activeSection === 'payments' && <PaymentsSection isActivated={true} headers={headers} />}
          {activeSection === 'maintenance' && (
            <div>
              <WarrantyBlock headers={headers} />
              <MaintenanceSection isActivated={true} headers={headers} />
            </div>
          )}
          {activeSection === 'appointments' && <AppointmentsSection headers={headers} />}
          {activeSection === 'team' && <TeamSection headers={headers} />}
          {activeSection === 'cleaning' && <CleaningSection headers={headers} setActiveSection={setActiveSection} />}
          {activeSection === 'gallery' && <ProjectGallerySection isActivated={isActivated} headers={headers} />}
          {activeSection === 'promotions' && <PromotionsSection headers={headers} />}
          {activeSection === 'profile' && (
            <AccountSettingsSection
              profile={profile} reloadProfile={loadProfile} headers={headers} isActivated={isActivated}
              uanCode={uanCode} setUanCode={setUanCode}
              activateUAN={activateUAN} uanLoading={uanLoading} uanError={uanError}
            />
          )}
        </div>
      </main>
    </div>
  );
}


/* ======================== HOME DASHBOARD ======================== */
function HomeDashboard({ profile, setActiveSection, isActivated, headers }) {
  const [bookings, setBookings] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [bRes, uRes] = await Promise.all([
          axios.get(`${API}/client/bookings`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/client/unread-count`, { headers }).catch(() => ({ data: { unread: 0 } })),
        ]);
        setBookings(bRes.data.slice(0, 3));
        setUnread(uRes.data.unread);
      } catch (e) { /* ignored */ }
    };
    load();
  }, []);

  const quickLinks = [
    { label: 'Book Service', section: 'booking', icon: Calendar },
    { label: 'Inbox', section: 'inbox', icon: MessageSquare, badge: unread > 0 ? unread : null },
    { label: 'Documents', section: 'documents', icon: FileText },
    { label: 'Payments', section: 'payments', icon: CreditCard },
    { label: 'Promotions', section: 'promotions', icon: Tag },
    { label: 'Maintenance', section: 'maintenance', icon: Wrench },
  ];

  return (
    <div data-testid="portal-home">
      <div className="mb-8">
        <h2 className="font-playfair text-2xl text-white">Welcome back, {profile?.name || 'Client'}</h2>
        <p className="text-white/30 font-manrope text-sm mt-1">Your Dream Room Theaters client portal</p>
      </div>

      {!isActivated && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-sm p-5" data-testid="uan-prompt">
          <div className="flex items-start gap-3">
            <Lock size={16} className="text-[#C9A84C] mt-0.5" />
            <div>
              <h3 className="font-manrope text-white text-sm font-bold mb-1">Unlock Full Project Access</h3>
              <p className="text-white/40 font-manrope text-xs">Your account includes all standard services — care plans, cleaning, documents, booklets and messaging. Theater project features (project photos, project documents, your team) unlock automatically when your site survey is booked and paid, or enter a project activation code in My Profile.</p>
              <button onClick={() => setActiveSection('profile')} className="mt-3 text-[#C9A84C] font-manrope text-xs uppercase tracking-widest hover:text-[#D4B560]">Go to Profile &rarr;</button>
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {quickLinks.map(link => {
          const Icon = link.icon;
          return (
            <motion.button
              key={link.label}
              whileHover={{ scale: 1.02 }}
              onClick={() => setActiveSection(link.section)}
              className={`bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-5 text-left transition-all duration-300 hover:border-[#C9A84C]/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#C9A84C]/5 relative ${link.locked ? 'opacity-40' : ''}`}
              data-testid={`quick-${link.section}`}
            >
              <Icon size={16} className="text-[#C9A84C] mb-3" />
              <p className="text-white font-manrope text-xs uppercase tracking-widest">{link.label}</p>
              {link.badge && (
                <span className="absolute top-3 right-3 bg-[#C9A84C] text-black w-5 h-5 rounded-full flex items-center justify-center font-manrope text-[10px] font-bold">{link.badge}</span>
              )}
              {link.locked && <Lock size={10} className="absolute top-3 right-3 text-white/20" />}
            </motion.button>
          );
        })}
      </div>

      <h3 className="font-manrope text-white/30 text-[10px] uppercase tracking-widest mb-3">Recent Bookings</h3>
      {bookings.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center">
          <p className="text-white/30 font-manrope text-sm">No bookings yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map(b => (
            <div key={b.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-4 flex items-center justify-between hover:border-[#c9a84c]/20 transition-all duration-200">
              <div>
                <p className="text-white font-manrope text-sm capitalize">{b.service_type}</p>
                <p className="text-white/30 font-manrope text-xs">{b.preferred_date || 'TBD'}</p>
              </div>
              <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${b.status === 'confirmed' ? 'bg-green-500/10 text-green-500' : b.status === 'completed' ? 'bg-blue-500/10 text-blue-400' : 'bg-yellow-500/10 text-yellow-500'}`}>{b.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ======================== SERVICES ======================== */
const PLAN_META = {
  'platinum-prestige': 'Our flagship warranty: lifetime craftsmanship coverage, 24/7 concierge support, white-glove quarterly care, full component replacement, upgrade assurance, and VIP treatment.',
  'maintenance-standard': 'Basic annual upkeep: system check-up, calibration, equipment dusting, chair wipe-down, troubleshooting support, and floor cleaning.',
  'maintenance-premium': 'Everything in Standard plus 2 tune-ups/year, deep cleaning, chair sanitizing, remote diagnostics, 10% labor discount, and emergency visits.',
  'maintenance-elite': 'Zero compromise: quarterly visits (up to 8/year), full performance audits, unlimited remote support, same-day priority response, and 15% parts & labor discount.',
};

function ServicesSection({ isActivated, headers, profile }) {
  const [services, setServices] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    if (isActivated) axios.get(`${API}/services`).then(r => setServices(r.data)).catch(() => {});
  }, [isActivated]);

  if (!isActivated) return <LockedSection message="Enter your UAN code to see pricing and packages." />;

  const handlePurchase = async (pkgId) => {
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { package_id: pkgId, billing_period: billing, origin_url: window.location.origin, client_email: profile?.email || '', client_name: profile?.name || '' });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { alert(err.response?.data?.detail || 'Payment error'); }
    finally { setLoadingId(null); }
  };

  const fmt = (n) => n?.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="portal-services">
      <p className="text-white/30 font-manrope text-sm mb-4">Purchase warranty and maintenance plans. Choose monthly billing or save 20% with an annual contract.</p>
      <div className="inline-flex rounded-sm border border-[#C9A84C]/30 overflow-hidden mb-6" data-testid="billing-toggle">
        <button onClick={() => setBilling('monthly')} className={`px-5 py-2 font-manrope text-[10px] uppercase tracking-widest transition-all ${billing === 'monthly' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/50 hover:text-white'}`} data-testid="billing-monthly">
          Monthly
        </button>
        <button onClick={() => setBilling('annual')} className={`px-5 py-2 font-manrope text-[10px] uppercase tracking-widest transition-all ${billing === 'annual' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/50 hover:text-white'}`} data-testid="billing-annual">
          Annual — Save 20%
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {services && Object.values(services).map(pkg => (
          <div key={pkg.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-5 hover:border-[#C9A84C]/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#C9A84C]/5 transition-all duration-300" data-testid={`portal-pkg-${pkg.id}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h4 className="font-playfair text-white text-base">{pkg.name}</h4>
              <span className="text-[8px] uppercase tracking-widest font-manrope text-[#C9A84C]/70 border border-[#C9A84C]/20 rounded-sm px-2 py-1 whitespace-nowrap">{pkg.category}</span>
            </div>
            <div className="mb-3">
              {billing === 'monthly' ? (
                <span className="text-[#C9A84C] font-playfair text-2xl font-bold">${fmt(pkg.monthly)}<span className="text-xs text-white/40 font-manrope font-normal"> /month</span></span>
              ) : (
                <div>
                  <span className="text-[#C9A84C] font-playfair text-2xl font-bold">${fmt(pkg.annual)}<span className="text-xs text-white/40 font-manrope font-normal"> /year</span></span>
                  <span className="ml-2 text-white/25 font-manrope text-xs line-through">${fmt(pkg.annual_full)}</span>
                </div>
              )}
            </div>
            <p className="text-white/40 font-manrope text-xs mb-4 leading-relaxed">{PLAN_META[pkg.id] || ''}</p>
            <button onClick={() => handlePurchase(pkg.id)} disabled={loadingId === pkg.id} className="w-full bg-[#C9A84C] hover:bg-[#D4B560] text-black font-manrope font-bold text-[10px] uppercase tracking-widest py-2.5 rounded-sm transition-all disabled:opacity-50" data-testid={`buy-${pkg.id}`}>
              {loadingId === pkg.id ? 'Redirecting...' : billing === 'monthly' ? 'Purchase — Monthly' : 'Purchase — Annual (20% off)'}
            </button>
          </div>
        ))}
      </div>
      <p className="text-white/25 font-manrope text-xs mt-5">Monthly plans: a secure Square payment link will be sent to you each month — pay online or over the phone.</p>
    </div>
  );
}


/* ======================== BOOKING ======================== */
function BookingSection({ headers, profile }) {
  const [form, setForm] = useState({ service_type: 'consultation', preferred_date: '', preferred_time: '', notes: '' });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Use public bookings endpoint with client profile info
      await axios.post(`${API}/bookings`, {
        full_name: profile?.name || '',
        email: profile?.email || '',
        phone: profile?.phone || '',
        ...form
      });
      setSuccess(true);
      setForm({ service_type: 'consultation', preferred_date: '', preferred_time: '', notes: '' });
    } catch (err) { setError(err.response?.data?.detail || 'Booking failed'); }
  };

  return (
    <div data-testid="portal-booking" className="max-w-lg">
      <p className="text-white/30 font-manrope text-sm mb-6">Schedule a service or consultation.</p>
      {success && <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-sm p-4 text-green-400 font-manrope text-sm" data-testid="booking-success">Booking submitted successfully!</div>}
      {error && <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-red-400 font-manrope text-sm">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-2">Service Type</label>
          <select value={form.service_type} onChange={e => setForm(p => ({ ...p, service_type: e.target.value }))} className="w-full bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-3 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C]" data-testid="booking-service-select">
            <option value="consultation">Consultation</option>
            <option value="installation">Installation</option>
            <option value="maintenance">Maintenance</option>
            <option value="cleaning">Cleaning</option>
            <option value="repair">Repair</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-2">Date</label>
            <input type="date" value={form.preferred_date} onChange={e => setForm(p => ({ ...p, preferred_date: e.target.value }))} className="w-full bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-3 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C]" data-testid="booking-date" />
          </div>
          <div>
            <label className="block text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-2">Time</label>
            <input type="time" value={form.preferred_time} onChange={e => setForm(p => ({ ...p, preferred_time: e.target.value }))} className="w-full bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-3 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C]" data-testid="booking-time" />
          </div>
        </div>
        <div>
          <label className="block text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-2">Notes</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-3 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C] resize-none" placeholder="Describe what you need..." data-testid="booking-notes" />
        </div>
        <button type="submit" className="w-full bg-[#C9A84C] hover:bg-[#D4B560] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm transition-all" data-testid="submit-booking">
          Book Now
        </button>
      </form>
    </div>
  );
}


/* ======================== INBOX ======================== */
function InboxSection({ headers, profile }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/messages`, { headers });
      setMessages(res.data.messages || []);
    } catch (e) { /* ignored */ }
  }, []);

  useEffect(() => { loadMessages(); const iv = setInterval(loadMessages, 10000); return () => clearInterval(iv); }, [loadMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (attachmentId = null) => {
    if (!newMsg.trim() && !attachmentId) return;
    setSending(true);
    try {
      await axios.post(`${API}/client/messages`, { recipient_id: 'admin', content: newMsg, attachment_id: attachmentId }, { headers });
      setNewMsg('');
      loadMessages();
    } catch (e) { /* ignored */ }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { alert('File too large. Max 25MB.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('context', 'message');
      const res = await axios.post(`${API}/client-upload`, fd, { headers });
      await sendMessage(res.data.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); fileInputRef.current && (fileInputRef.current.value = ''); }
  };

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const isImage = (ct) => ct?.startsWith('image/');

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 180px)' }} data-testid="portal-inbox">
      <p className="text-white/30 font-manrope text-sm mb-4">Messages with DreamRoom Team</p>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-4 space-y-3 mb-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare size={24} className="text-white/10 mx-auto mb-3" />
            <p className="text-white/20 font-manrope text-sm">No messages yet. Start a conversation!</p>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] px-4 py-3 rounded-sm ${msg.sender_type === 'client' ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/20' : 'bg-white/[0.03] border border-[#c9a84c]/10'}`}>
              {msg.content && <p className="text-white/70 font-manrope text-sm leading-relaxed">{msg.content}</p>}
              {msg.attachment && (
                <div className="mt-2 bg-[#0A0608]/40 border border-[#c9a84c]/10 rounded-sm p-2">
                  {isImage(msg.attachment.content_type) ? (
                    <img src={`${API}/files/${msg.attachment.storage_path}`} alt={msg.attachment.filename} className="max-w-full max-h-48 rounded-sm" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-[#C9A84C]/60 shrink-0" />
                      <span className="text-white/50 font-manrope text-xs truncate flex-1">{msg.attachment.filename}</span>
                    </div>
                  )}
                  <a href={`${API}/files/${msg.attachment.storage_path}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 mt-1.5 text-[#C9A84C]/60 hover:text-[#C9A84C] font-manrope text-[10px] uppercase tracking-wider transition-colors" data-testid="download-attachment">
                    <Download size={10} /> Download
                  </a>
                </div>
              )}
              <p className={`font-manrope text-[10px] mt-1 ${msg.sender_type === 'client' ? 'text-[#C9A84C]/40 text-right' : 'text-white/20'}`}>
                {msg.sender_name} &middot; {formatTime(msg.created_at)}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-[#1A0F12] border border-[#c9a84c]/20 hover:border-[#C9A84C]/40 text-white/40 hover:text-[#C9A84C] px-3 rounded-sm transition-all disabled:opacity-30"
          data-testid="attach-file-btn"
          title="Attach file"
        >
          {uploading ? <span className="animate-spin inline-block w-4 h-4 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full" /> : <Paperclip size={16} />}
        </button>
        <input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Type your message..."
          className="flex-1 bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-3 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C]"
          data-testid="message-input"
        />
        <button
          onClick={() => sendMessage()} disabled={sending || !newMsg.trim()}
          className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-5 rounded-sm transition-all disabled:opacity-30"
          data-testid="send-message-btn"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}


/* ======================== DOCUMENT LIBRARY ======================== */
function LibrarySection({ headers, isActivated }) {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [docsRes, catsRes] = await Promise.all([
        axios.get(`${API}/client/library`, { headers }),
        axios.get(`${API}/client/library/categories`, { headers }),
      ]);
      setDocuments(docsRes.data);
      setCategories(catsRes.data);
    } catch (e) { /* ignored */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filtered = documents.filter(d => {
    if (activeCategory !== 'all' && d.category_id !== activeCategory) return false;
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !d.description?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const isPdf = (ct) => ct === 'application/pdf';
  const isImage = (ct) => ct?.startsWith('image/');

  return (
    <div data-testid="portal-library">
      <p className="text-white/30 font-manrope text-sm mb-5">Browse DreamRoom Theaters documents, brochures, and guides</p>

      {/* Search */}
      <div className="mb-5">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search documents..."
          className="w-full max-w-md bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm px-4 py-2.5 text-white font-manrope text-sm focus:outline-none focus:border-[#C9A84C] placeholder:text-white/20"
          data-testid="library-search"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-all ${activeCategory === 'all' ? 'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C]' : 'bg-[#1A0F12] border-[#c9a84c]/10 text-white/35 hover:text-white/60'}`}
          data-testid="cat-all">All</button>
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
            className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-all ${activeCategory === cat.id ? 'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C]' : 'bg-[#1A0F12] border-[#c9a84c]/10 text-white/35 hover:text-white/60'}`}
            data-testid={`cat-${cat.slug}`}>{cat.name}</button>
        ))}
      </div>

      {/* Docs Grid */}
      {filtered.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-12 text-center">
          <FolderOpen size={28} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/20 font-manrope text-sm">No documents in this category</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(doc => (
            <div key={doc.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-5 hover:border-[#C9A84C]/25 transition-all duration-200 group" data-testid={`lib-doc-${doc.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-[#C9A84C]/8 border border-[#C9A84C]/15 flex items-center justify-center shrink-0">
                  {isPdf(doc.content_type) ? <FileText size={16} className="text-[#C9A84C]/60" /> : isImage(doc.content_type) ? <FileImage size={16} className="text-[#C9A84C]/60" /> : <FolderOpen size={16} className="text-[#C9A84C]/60" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-manrope text-sm font-semibold text-white/80 truncate">{doc.title}</h4>
                  {doc.category_name && <span className="font-manrope text-[10px] text-[#C9A84C]/40 uppercase tracking-wider">{doc.category_name}</span>}
                </div>
              </div>
              {doc.description && <p className="text-white/25 font-manrope text-xs leading-relaxed mb-3 line-clamp-2">{doc.description}</p>}
              <div className="flex items-center justify-between text-white/20 font-manrope text-[10px]">
                <span>v{doc.version} &middot; {formatDate(doc.last_updated)}</span>
                <span>{formatSize(doc.file_size)}</span>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-[#c9a84c]/5">
                <button onClick={() => setViewingDoc(doc)}
                  className="flex items-center gap-1.5 text-[#C9A84C]/50 hover:text-[#C9A84C] font-manrope text-[10px] uppercase tracking-wider transition-colors"
                  data-testid={`view-doc-${doc.id}`}>
                  <Eye size={12} /> View
                </button>
                {doc.is_downloadable && (
                  <a href={`${API}/files/${doc.storage_path}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-[#C9A84C]/50 hover:text-[#C9A84C] font-manrope text-[10px] uppercase tracking-wider transition-colors"
                    data-testid={`dl-doc-${doc.id}`}>
                    <Download size={12} /> Download
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document Viewer Modal */}
      <AnimatePresence>
        {viewingDoc && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col" onClick={() => setViewingDoc(null)}>
            <div className="flex items-center justify-between p-4 border-b border-[#c9a84c]/10" onClick={e => e.stopPropagation()}>
              <div>
                <h3 className="font-manrope text-white text-sm font-semibold">{viewingDoc.title}</h3>
                <p className="text-white/30 font-manrope text-[10px]">Version {viewingDoc.version} &middot; {formatDate(viewingDoc.last_updated)}</p>
              </div>
              <div className="flex items-center gap-3">
                {viewingDoc.is_downloadable && (
                  <a href={`${API}/files/${viewingDoc.storage_path}`} target="_blank" rel="noreferrer" className="text-[#C9A84C]/60 hover:text-[#C9A84C] transition-colors">
                    <Download size={18} />
                  </a>
                )}
                <button onClick={() => setViewingDoc(null)} className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4" onClick={e => e.stopPropagation()}>
              {isPdf(viewingDoc.content_type) ? (
                <iframe src={`${API}/files/${viewingDoc.storage_path}`} className="w-full h-full min-h-[70vh] rounded-sm bg-white" title={viewingDoc.title} />
              ) : isImage(viewingDoc.content_type) ? (
                <div className="flex items-center justify-center h-full">
                  <img src={`${API}/files/${viewingDoc.storage_path}`} alt={viewingDoc.title} className="max-w-full max-h-[80vh] rounded-sm" />
                </div>
              ) : (
                <div className="text-center py-20">
                  <FileText size={32} className="text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 font-manrope text-sm mb-3">Preview not available for this file type</p>
                  {viewingDoc.is_downloadable && (
                    <a href={`${API}/files/${viewingDoc.storage_path}`} target="_blank" rel="noreferrer" className="text-[#C9A84C] font-manrope text-sm hover:underline">Download to view</a>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ======================== THEATER BOOKLETS ======================== */
function BookletsSection({ headers, isActivated }) {
  const [booklets, setBooklets] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);

  const loadBooklets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/booklets`, { headers });
      setBooklets(res.data);
    } catch (e) { /* ignored */ }
  }, []);

  useEffect(() => { loadBooklets(); }, [loadBooklets]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  const isPdf = (ct) => ct === 'application/pdf';
  const isImage = (ct) => ct?.startsWith('image/');

  return (
    <div data-testid="portal-booklets">
      <p className="text-white/30 font-manrope text-sm mb-5">Theater room inspiration, product catalogues, seating, audio/video equipment, lighting, design ideas & more</p>

      {booklets.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-12 text-center">
          <BookOpen size={28} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/20 font-manrope text-sm">No booklets available yet</p>
          <p className="text-white/10 font-manrope text-xs mt-1">Check back soon for theater inspiration and product catalogues</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {booklets.map(doc => (
            <button key={doc.id} onClick={() => setViewingDoc(doc)}
              className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm overflow-hidden hover:border-[#C9A84C]/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#C9A84C]/5 transition-all duration-300 text-left group"
              data-testid={`booklet-${doc.id}`}>
              {/* Cover / Preview */}
              <div className="h-40 bg-gradient-to-br from-[#1A0F12] to-[#231419] flex items-center justify-center border-b border-[#c9a84c]/5">
                {isImage(doc.content_type) ? (
                  <img src={`${API}/files/${doc.storage_path}`} alt={doc.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <BookOpen size={32} className="text-[#C9A84C]/20 mx-auto mb-2 group-hover:text-[#C9A84C]/40 transition-colors" />
                    <span className="text-white/10 font-manrope text-[10px] uppercase tracking-widest">PDF Booklet</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-manrope text-sm font-semibold text-white/80 mb-1 group-hover:text-[#C9A84C]/80 transition-colors">{doc.title}</h4>
                {doc.description && <p className="text-white/25 font-manrope text-xs leading-relaxed line-clamp-2 mb-2">{doc.description}</p>}
                <div className="flex items-center gap-2 text-[#C9A84C]/40 font-manrope text-[10px]">
                  <Eye size={10} /> <span>View Booklet</span>
                  <span className="ml-auto text-white/15">v{doc.version} &middot; {formatDate(doc.last_updated)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Viewer */}
      <AnimatePresence>
        {viewingDoc && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col" onClick={() => setViewingDoc(null)}>
            <div className="flex items-center justify-between p-4 border-b border-[#c9a84c]/10" onClick={e => e.stopPropagation()}>
              <div>
                <h3 className="font-manrope text-white text-sm font-semibold">{viewingDoc.title}</h3>
                <p className="text-white/30 font-manrope text-[10px]">Theater Booklet</p>
              </div>
              <div className="flex items-center gap-3">
                {viewingDoc.is_downloadable && (
                  <a href={`${API}/files/${viewingDoc.storage_path}`} target="_blank" rel="noreferrer" className="text-[#C9A84C]/60 hover:text-[#C9A84C] transition-colors">
                    <Download size={18} />
                  </a>
                )}
                <button onClick={() => setViewingDoc(null)} className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4" onClick={e => e.stopPropagation()}>
              {isPdf(viewingDoc.content_type) ? (
                <iframe src={`${API}/files/${viewingDoc.storage_path}`} className="w-full h-full min-h-[70vh] rounded-sm bg-white" title={viewingDoc.title} />
              ) : isImage(viewingDoc.content_type) ? (
                <div className="flex items-center justify-center h-full">
                  <img src={`${API}/files/${viewingDoc.storage_path}`} alt={viewingDoc.title} className="max-w-full max-h-[80vh] rounded-sm" />
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-white/30 font-manrope text-sm">Preview not available</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ======================== DOCUMENTS ======================== */
function DocumentsSection({ isActivated, headers, profile }) {
  const [documents, setDocuments] = useState([]);
  const [activeFolder, setActiveFolder] = useState('all');
  const [signingDoc, setSigningDoc] = useState(null);
  const [sigType, setSigType] = useState('drawn');
  const [sigData, setSigData] = useState('');
  const [agreedText, setAgreedText] = useState('');
  const [agreed, setAgreed] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const loadDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/documents`, { headers });
      setDocuments(res.data);
    } catch (e) { /* ignored */ }
  }, []);

  useEffect(() => { if (isActivated) loadDocs(); }, [isActivated, loadDocs]);

  if (!isActivated) return <LockedSection message="Enter your UAN code to access your documents and contracts." />;

  const folders = ['all', 'general', 'pre-construction', 'installation', 'completion', 'warranty'];
  const filtered = activeFolder === 'all' ? documents : documents.filter(d => d.folder === activeFolder);

  // Canvas drawing helpers
  const startDraw = (e) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#C9A84C';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
    if (canvasRef.current) setSigData(canvasRef.current.toDataURL());
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSigData('');
  };

  const signDocument = async () => {
    if (!signingDoc) return;
    try {
      await axios.post(`${API}/client/documents/${signingDoc.id}/sign`, {
        signature_type: sigType,
        signature_data: sigType === 'drawn' ? sigData : '',
        agreed_text: sigType === 'checkbox' ? `I, ${profile?.name}, agree to the terms of document "${signingDoc.title}" on ${new Date().toLocaleDateString()}` : '',
      }, { headers });
      setSigningDoc(null);
      setSigData('');
      setAgreed(false);
      loadDocs();
    } catch (err) { alert(err.response?.data?.detail || 'Signing failed'); }
  };

  return (
    <div data-testid="portal-documents">
      <p className="text-white/30 font-manrope text-sm mb-4">Your contracts, proposals, and project files.</p>

      <SignatureRequestsBlock headers={headers} profile={profile} />

      {/* Folder Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {folders.map(f => (
          <button key={f} onClick={() => setActiveFolder(f)} className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-all whitespace-nowrap ${activeFolder === f ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/30 border border-[#c9a84c]/20 hover:text-white/60'}`} data-testid={`folder-${f}`}>
            {f === 'all' ? 'All Files' : folderLabels[f] || f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No documents in this folder</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => (
            <div key={doc.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-4 flex items-center gap-4 hover:border-[#C9A84C]/25 transition-all duration-200" data-testid={`doc-${doc.id}`}>
              <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: (docTypeColors[doc.doc_type] || '#666') + '15', border: `1px solid ${docTypeColors[doc.doc_type] || '#666'}30` }}>
                <FileText size={16} style={{ color: docTypeColors[doc.doc_type] || '#666' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-manrope text-sm font-semibold truncate">{doc.title}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] font-manrope uppercase tracking-widest" style={{ color: docTypeColors[doc.doc_type] || '#666' }}>{doc.doc_type}</span>
                  <span className="text-white/20 font-manrope text-[10px]">{folderLabels[doc.folder] || doc.folder}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {doc.signature_required && doc.signature_status === 'pending' && (
                  <button onClick={() => setSigningDoc(doc)} className="flex items-center gap-1 bg-[#C9A84C] text-black px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest font-bold hover:bg-[#D4B560]" data-testid={`sign-${doc.id}`}>
                    <Pen size={10} /> Sign
                  </button>
                )}
                {doc.signature_status === 'signed' && (
                  <span className="flex items-center gap-1 text-green-400 font-manrope text-[10px] uppercase tracking-widest"><Check size={10} /> Signed</span>
                )}
                {doc.file_url && (
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] font-manrope text-[10px] uppercase tracking-widest hover:underline">View</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Signature Modal */}
      <AnimatePresence>
        {signingDoc && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setSigningDoc(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} className="bg-[#1A0F12] border border-[#C9A84C]/20 rounded-sm w-full max-w-lg p-6 shadow-2xl shadow-[#C9A84C]/5" onClick={e => e.stopPropagation()} data-testid="signature-modal">
              <h3 className="font-playfair text-white text-lg mb-2">Sign Document</h3>
              <p className="text-white/40 font-manrope text-xs mb-6">{signingDoc.title}</p>

              {/* Signature Type Toggle */}
              <div className="flex gap-2 mb-6">
                <button onClick={() => setSigType('drawn')} className={`flex-1 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest ${sigType === 'drawn' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/40 border border-[#c9a84c]/20'}`} data-testid="sig-type-drawn">
                  Draw Signature
                </button>
                <button onClick={() => setSigType('checkbox')} className={`flex-1 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest ${sigType === 'checkbox' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/40 border border-[#c9a84c]/20'}`} data-testid="sig-type-checkbox">
                  Accept & Agree
                </button>
              </div>

              {sigType === 'drawn' ? (
                <div>
                  <p className="text-white/30 font-manrope text-xs mb-2">Draw your signature below:</p>
                  <canvas
                    ref={canvasRef}
                    width={440}
                    height={150}
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    className="w-full border border-[#c9a84c]/20 rounded-sm bg-white/[0.02] cursor-crosshair"
                    data-testid="signature-canvas"
                  />
                  <button onClick={clearCanvas} className="mt-2 text-white/30 font-manrope text-[10px] uppercase tracking-widest hover:text-white/60">Clear</button>
                </div>
              ) : (
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 accent-[#C9A84C]" data-testid="agree-checkbox" />
                    <span className="text-white/60 font-manrope text-sm leading-relaxed">
                      I, <strong className="text-white">{profile?.name}</strong>, have read and agree to the terms outlined in &ldquo;<strong className="text-[#C9A84C]">{signingDoc.title}</strong>&rdquo;.
                    </span>
                  </label>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={signDocument}
                  disabled={sigType === 'drawn' ? !sigData : !agreed}
                  className="flex-1 bg-[#C9A84C] hover:bg-[#D4B560] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm transition-all disabled:opacity-30"
                  data-testid="confirm-sign-btn"
                >
                  Sign Document
                </button>
                <button onClick={() => setSigningDoc(null)} className="px-6 text-white/40 border border-[#c9a84c]/20 rounded-sm font-manrope text-xs uppercase tracking-widest hover:border-white/30">
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ======================== PAYMENTS ======================== */
function PaymentsSection({ isActivated, headers }) {
  const [payments, setPayments] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    if (!isActivated) return;
    const load = async () => {
      try { const res = await axios.get(`${API}/client/payments`, { headers }); setPayments(res.data); }
      catch (e) { /* ignored */ }
      try { const res = await axios.get(`${API}/client/subscriptions`, { headers }); setSubs(res.data); }
      catch (e) { /* ignored */ }
    };
    load();
  }, [isActivated]);

  if (!isActivated) return <LockedSection message="Enter your UAN code to view payment history." />;

  const fmtAmt = (n) => n?.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="portal-payments">
      {subs.length > 0 && (
        <div className="mb-8" data-testid="portal-subscriptions">
          <p className="text-white/30 font-manrope text-sm mb-4">Your active plans.</p>
          <div className="grid md:grid-cols-2 gap-4 mb-2">
            {subs.map(s => (
              <div key={s.id} className="bg-[#1A0F12] border border-[#c9a84c]/20 rounded-sm p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-playfair text-white text-base">{s.package_name}</h4>
                  <span className={`px-2 py-1 rounded-sm text-[9px] uppercase tracking-widest font-manrope font-bold ${s.status === 'active' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400'}`}>{s.status}</span>
                </div>
                <p className="text-[#C9A84C] font-playfair text-xl font-bold mb-1">
                  ${fmtAmt(s.amount)}<span className="text-xs text-white/40 font-manrope font-normal"> /{s.billing_period === 'monthly' ? 'month' : 'year'}</span>
                </p>
                <p className="text-white/35 font-manrope text-xs">Next billing: {s.next_billing_date?.slice(0, 10)}</p>
                {s.last_link_url && (
                  <a href={s.last_link_url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-[#C9A84C] font-manrope text-[10px] uppercase tracking-widest underline underline-offset-4" data-testid={`sub-pay-link-${s.id}`}>
                    Pay Latest Invoice →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-white/30 font-manrope text-sm mb-6">Your transaction history.</p>
      {payments.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No payments yet</div>
      ) : (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#c9a84c]/10">
                {['Package', 'Amount', 'Status', 'Date'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-white/30 font-manrope">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} className="border-b border-[#c9a84c]/10">
                  <td className="px-4 py-3 text-white/80 font-manrope text-sm">{p.package_name}</td>
                  <td className="px-4 py-3 text-[#C9A84C] font-manrope font-semibold">${p.amount}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${p.payment_status === 'paid' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>{p.payment_status}</span>
                  </td>
                  <td className="px-4 py-3 text-white/30 font-manrope text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ======================== MAINTENANCE ======================== */
function MaintenanceSection({ isActivated, headers }) {
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!isActivated) return;
    const load = async () => {
      try { const res = await axios.get(`${API}/client/maintenance`, { headers }); setRecords(res.data); }
      catch (e) { /* ignored */ }
    };
    load();
  }, [isActivated]);

  if (!isActivated) return <LockedSection message="Enter your UAN code to view maintenance records." />;

  return (
    <div data-testid="portal-maintenance">
      <p className="text-white/30 font-manrope text-sm mb-6">Your service and maintenance history.</p>
      {records.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No maintenance records yet</div>
      ) : (
        <div className="space-y-3">
          {records.map(r => (
            <div key={r.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="bg-[#C9A84C]/10 text-[#C9A84C] px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold">{r.service_type}</span>
                <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${maintenanceStatusColors[r.status]?.bg || ''} ${maintenanceStatusColors[r.status]?.text || 'text-white/40'}`}>{r.status?.replace('_', ' ')}</span>
              </div>
              <p className="text-white font-manrope text-sm mb-2">{r.description}</p>
              <div className="flex gap-6 text-white/30 font-manrope text-xs">
                {r.scheduled_date && <span>Scheduled: {r.scheduled_date}</span>}
                {r.technician && <span>Tech: {r.technician}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ======================== PROJECT GALLERY ======================== */
function ProjectGallerySection({ isActivated, headers }) {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!isActivated) return;
    const load = async () => {
      try { const res = await axios.get(`${API}/client/project-gallery`, { headers }); setPhotos(res.data); }
      catch (e) { /* ignored */ }
    };
    load();
  }, [isActivated]);

  if (!isActivated) return <LockedSection message="Enter your UAN code to view your project gallery." />;

  const filtered = filter === 'all' ? photos : photos.filter(p => p.phase === filter);

  return (
    <div data-testid="portal-gallery">
      <p className="text-white/30 font-manrope text-sm mb-4">Photos from your theater installation.</p>
      <div className="flex gap-2 mb-6">
        {['all', 'before', 'during', 'completed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest ${filter === f ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/30 border border-[#c9a84c]/20'}`}>
            {f === 'all' ? 'All' : f === 'during' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No project photos yet</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {filtered.map(photo => (
            <div key={photo.id} className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm overflow-hidden">
              <div className="aspect-square"><img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" /></div>
              <div className="p-3">
                <p className="text-white font-manrope text-xs font-semibold truncate">{photo.title}</p>
                <span className={`text-[10px] uppercase tracking-widest font-manrope font-bold ${photo.phase === 'completed' ? 'text-green-400' : photo.phase === 'during' ? 'text-indigo-400' : 'text-yellow-400'}`}>{photo.phase}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ======================== PROMOTIONS ======================== */
function PromotionsSection({ headers }) {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const res = await axios.get(`${API}/client/promotions`, { headers }); setPromos(res.data); }
      catch (e) { /* ignored */ }
    };
    load();
  }, []);

  return (
    <div data-testid="portal-promotions">
      <p className="text-white/30 font-manrope text-sm mb-6">Exclusive deals and offers for you.</p>
      {promos.length === 0 ? (
        <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-8 text-center text-white/30 font-manrope text-sm">No active promotions</div>
      ) : (
        <div className="space-y-4">
          {promos.map(p => (
            <div key={p.id} className="bg-[#1A0F12] border border-[#C9A84C]/10 rounded-sm p-5">
              {p.discount_type !== 'info' && p.discount_value > 0 && (
                <span className="inline-block bg-[#C9A84C] text-black px-3 py-1 rounded-sm font-manrope font-bold text-xs mb-3 uppercase tracking-widest">
                  {p.discount_type === 'percentage' ? `${p.discount_value}% OFF` : `$${p.discount_value} OFF`}
                </span>
              )}
              <h4 className="font-playfair text-white text-lg mb-2">{p.title}</h4>
              <p className="text-white/40 font-manrope text-sm mb-3 leading-relaxed">{p.description}</p>
              <div className="flex items-center gap-4 text-white/30 font-manrope text-xs">
                {p.promo_code && <span>Code: <span className="text-[#C9A84C] font-mono font-bold">{p.promo_code}</span></span>}
                {p.valid_until && <span>Valid until {p.valid_until}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ======================== PROFILE ======================== */
function ProfileSection({ profile, isActivated, uanCode, setUanCode, activateUAN, uanLoading, uanError }) {
  return (
    <div data-testid="portal-profile" className="max-w-lg">
      <div className="bg-[#1A0F12] border border-[#c9a84c]/10 rounded-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
            <span className="font-playfair text-[#C9A84C] text-xl font-bold">{profile?.name?.charAt(0) || 'C'}</span>
          </div>
          <div>
            <h3 className="font-playfair text-white text-lg">{profile?.name}</h3>
            <p className="text-white/40 font-manrope text-sm">{profile?.email}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-[#c9a84c]/10">
            <span className="text-white/30 font-manrope text-xs uppercase tracking-widest">Phone</span>
            <span className="text-white/60 font-manrope text-sm">{profile?.phone || '—'}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-[#c9a84c]/10">
            <span className="text-white/30 font-manrope text-xs uppercase tracking-widest">Access Level</span>
            <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${isActivated ? 'bg-green-500/10 text-green-500' : 'bg-[#C9A84C]/10 text-[#C9A84C]'}`}>
              {isActivated ? 'Tier 2 — Full Project Access' : 'Tier 1 — Standard Access'}
            </span>
          </div>
          {profile?.uan && (
            <div className="flex justify-between items-center py-2 border-b border-[#c9a84c]/10">
              <span className="text-white/30 font-manrope text-xs uppercase tracking-widest">Your UAN</span>
              <span className="text-[#C9A84C] font-manrope text-sm font-mono font-bold" data-testid="profile-uan">{profile.uan}</span>
            </div>
          )}
          {profile?.uan_code && profile?.uan_code !== profile?.uan && (
            <div className="flex justify-between items-center py-2">
              <span className="text-white/30 font-manrope text-xs uppercase tracking-widest">Activation Code Used</span>
              <span className="text-white/40 font-manrope text-sm font-mono">{profile.uan_code}</span>
            </div>
          )}
        </div>
      </div>

      {!isActivated && (
        <div className="bg-[#1A0F12] border border-[#C9A84C]/20 rounded-sm p-6" data-testid="uan-activation">
          <h4 className="font-playfair text-white text-base mb-2">Unlock Project Access</h4>
          <p className="text-white/30 font-manrope text-xs mb-4">Have a project activation code from your Dream Room technician or booking confirmation email? Enter it here to unlock full project features.</p>
          {uanError && <p className="text-red-400 font-manrope text-xs mb-3">{uanError}</p>}
          <div className="flex gap-2">
            <input
              value={uanCode}
              onChange={e => setUanCode(e.target.value)}
              placeholder="DRT-XXXX-XXXX"
              className="flex-1 bg-transparent border-b border-white/20 focus:border-[#C9A84C] focus:outline-none px-0 py-2 text-white font-manrope text-sm font-mono placeholder:text-white/15 uppercase tracking-widest"
              data-testid="uan-input"
            />
            <button
              onClick={activateUAN}
              disabled={uanLoading || !uanCode}
              className="bg-[#C9A84C] hover:bg-[#D4B560] text-black px-6 py-2 rounded-sm font-manrope font-bold text-xs uppercase tracking-widest disabled:opacity-30"
              data-testid="activate-uan-btn"
            >
              {uanLoading ? '...' : 'Activate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ======================== LOCKED SECTION ======================== */
function LockedSection({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-20" data-testid="locked-section">
      <div className="w-16 h-16 rounded-full bg-[#1A0F12] border border-[#c9a84c]/10 flex items-center justify-center mb-5">
        <Lock size={24} className="text-[#C9A84C]/30" />
      </div>
      <h3 className="font-playfair text-white/50 text-lg mb-2">Feature Locked</h3>
      <p className="text-white/25 font-manrope text-sm text-center max-w-sm leading-relaxed">{message}</p>
    </div>
  );
}


/* ======================== SIGNATURE REQUESTS ======================== */
function SignatureRequestsBlock({ headers, profile }) {
  const [requests, setRequests] = useState([]);
  const [signing, setSigning] = useState(null);
  const [cFields, setCFields] = useState({});
  const [sigType, setSigType] = useState('drawn');
  const [sigData, setSigData] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const clientToken = localStorage.getItem('client_token');

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/client/signature-requests`, { headers }); setRequests(res.data); }
    catch (e) { /* ignored */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRequest = async (id) => {
    try {
      const res = await axios.get(`${API}/client/signature-requests/${id}`, { headers });
      const prefills = {};
      (res.data.client_field_schema || []).forEach(f => {
        if (f.prefill === 'name') prefills[f.key] = profile?.name || '';
      });
      setCFields(prefills);
      setSigData(''); setAgreed(false); setSigType('drawn');
      setSigning(res.data);
    } catch (e) { /* ignored */ }
  };

  const startDraw = (e) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };
  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#C9A84C';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };
  const endDraw = () => {
    if (isDrawing && canvasRef.current) setSigData(canvasRef.current.toDataURL('image/png'));
    setIsDrawing(false);
  };
  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSigData('');
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/signature-requests/${signing.id}/sign`, {
        client_fields: cFields,
        signature_type: sigType,
        signature_data: sigType === 'drawn' ? sigData : '',
      }, { headers });
      setSigning(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Signing failed'); }
    finally { setSubmitting(false); }
  };

  if (requests.length === 0) return null;

  const pending = requests.filter(r => r.status !== 'signed');

  return (
    <div className="mb-8" data-testid="signature-requests-block">
      <div className="flex items-center gap-2 mb-3">
        <Pen size={14} className="text-[#C9A84C]" />
        <h4 className="font-manrope text-[#C9A84C] text-[10px] uppercase tracking-[0.25em] font-bold">Signature Requests</h4>
        {pending.length > 0 && (
          <span className="bg-[#C9A84C] text-black text-[9px] font-bold rounded-full px-2 py-0.5 font-manrope">{pending.length} pending</span>
        )}
      </div>
      <div className="space-y-2">
        {requests.map(r => (
          <div key={r.id} className={`bg-[#1A0F12] border rounded-sm p-4 flex flex-wrap items-center justify-between gap-3 ${r.status === 'signed' ? 'border-[#c9a84c]/10' : 'border-[#C9A84C]/40'}`} data-testid={`client-sig-request-${r.id}`}>
            <div>
              <p className="font-manrope text-white text-sm font-bold">{r.title}</p>
              <p className="font-manrope text-white/30 text-xs">Received {r.created_at?.slice(0, 10)}</p>
            </div>
            <div className="flex items-center gap-2">
              {r.status === 'signed' ? (
                <>
                  <span className="flex items-center gap-1 text-green-400 font-manrope text-[10px] uppercase tracking-widest"><Check size={10} /> Signed</span>
                  <a href={`${API}/signature-requests/${r.id}/pdf?token=${clientToken}`} target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] font-manrope text-[10px] uppercase tracking-widest hover:underline" data-testid={`client-sig-pdf-${r.id}`}>Download PDF</a>
                </>
              ) : (
                <button onClick={() => openRequest(r.id)} className="flex items-center gap-1 bg-[#C9A84C] text-black px-4 py-2 rounded-sm font-manrope text-[10px] uppercase tracking-widest font-bold hover:bg-[#D4B560]" data-testid={`review-sign-${r.id}`}>
                  <Pen size={10} /> Review & Sign
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {signing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setSigning(null)}>
            <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.2 }} className="bg-[#1A0F12] border border-[#C9A84C]/20 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8" onClick={e => e.stopPropagation()} data-testid="sign-request-modal">
              <h3 className="font-playfair text-white text-xl mb-1">{signing.title}</h3>
              <p className="text-white/30 font-manrope text-xs mb-6">Please review the document carefully, complete your details, and sign below.</p>

              {/* Document content */}
              <div className="bg-black/30 border border-[#c9a84c]/10 rounded-sm p-5 max-h-72 overflow-y-auto mb-6 space-y-3" data-testid="sign-doc-content">
                {(signing.rendered_sections || []).map((s, i) => (
                  <div key={i}>
                    {s.heading && <h4 className="font-manrope text-white text-xs font-bold mb-1">{s.heading}</h4>}
                    {s.body.map((p, j) => <p key={j} className="font-manrope text-white/40 text-xs leading-relaxed mb-1">{p}</p>)}
                    {s.bullets.map((b, j) => <p key={j} className="font-manrope text-white/40 text-xs leading-relaxed pl-3 mb-1">• {b}</p>)}
                    {s.after && <p className="font-manrope text-white/40 text-xs leading-relaxed">{s.after}</p>}
                  </div>
                ))}
              </div>

              {/* Client fields */}
              <div className="space-y-4 mb-6">
                {(signing.client_field_schema || []).map(f => (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-widest text-white/40 font-manrope block mb-1">{f.label}</label>
                    <input
                      type="text"
                      value={cFields[f.key] || ''}
                      onChange={e => setCFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full bg-black/30 border border-[#c9a84c]/20 text-white font-manrope text-sm rounded-sm px-3 py-2.5 focus:border-[#C9A84C] focus:outline-none placeholder:text-white/20"
                      data-testid={`client-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>

              {/* Signature */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => setSigType('drawn')} className={`flex-1 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest ${sigType === 'drawn' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/40 border border-[#c9a84c]/20'}`} data-testid="req-sig-type-drawn">Draw Signature</button>
                <button onClick={() => setSigType('checkbox')} className={`flex-1 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest ${sigType === 'checkbox' ? 'bg-[#C9A84C] text-black font-bold' : 'text-white/40 border border-[#c9a84c]/20'}`} data-testid="req-sig-type-checkbox">Accept & Agree</button>
              </div>
              {sigType === 'drawn' ? (
                <div className="mb-6">
                  <canvas
                    ref={canvasRef} width={560} height={140}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                    className="w-full border border-[#c9a84c]/20 rounded-sm bg-white/[0.02] cursor-crosshair"
                    data-testid="req-signature-canvas"
                  />
                  <button onClick={clearCanvas} className="mt-2 text-white/30 font-manrope text-[10px] uppercase tracking-widest hover:text-white/60">Clear</button>
                </div>
              ) : (
                <label className="flex items-start gap-3 cursor-pointer mb-6">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 accent-[#C9A84C]" data-testid="req-agree-checkbox" />
                  <span className="text-white/60 font-manrope text-sm leading-relaxed">
                    I, <strong className="text-white">{profile?.name}</strong>, have read, understood, and agree to the terms of &ldquo;<strong className="text-[#C9A84C]">{signing.title}</strong>&rdquo; and sign it electronically.
                  </span>
                </label>
              )}

              <div className="flex gap-3">
                <button
                  onClick={submit}
                  disabled={submitting || (sigType === 'drawn' ? !sigData : !agreed)}
                  className="flex-1 bg-[#C9A84C] hover:bg-[#D4B560] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm transition-all disabled:opacity-30"
                  data-testid="submit-sign-request-btn"
                >
                  {submitting ? 'Signing...' : 'Sign Document'}
                </button>
                <button onClick={() => setSigning(null)} className="px-6 text-white/40 border border-[#c9a84c]/20 rounded-sm font-manrope text-xs uppercase tracking-widest hover:border-white/30">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

